import React, { useCallback, useEffect, useState } from "react";
import { Search, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";
import SmartImage from "../components/SmartImage";
import { borderStyle } from "./ui";

/** Modal media browser used by the page/product editors. */
export default function MediaPicker({ onPick, onClose }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi
      .get("/admin/media", { params: { search: q || undefined, limit: 60 } })
      .then((r) => setRows(r.data.media))
      .catch(() => setRows([]));
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  async function upload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await adminApi.post("/admin/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.deduped ? "Image already in library" : "Uploaded");
      onPick(data.media);
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" style={{ background: "rgba(5,7,6,0.9)" }}>
      <div
        className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-xl border"
        style={{ background: "var(--nq-ink)", borderColor: "rgba(198,162,74,0.3)" }}
        data-testid="media-picker"
      >
        <div className="flex items-center gap-3 border-b p-4" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <h2 className="text-sm font-semibold uppercase tracking-wider gold-text">Media library</h2>
          <div className="ml-auto flex items-center gap-2 rounded-lg border px-3 py-1.5" style={borderStyle}>
            <Search className="h-4 w-4 gold-text" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search"
              className="w-32 bg-transparent text-sm outline-none"
            />
          </div>
          <label className="btn-outline-nq cursor-pointer px-3 py-1.5 text-xs">
            <Upload className="h-3.5 w-3.5" /> {busy ? "Uploading..." : "Upload"}
            <input type="file" accept="image/*" className="hidden" onChange={upload} />
          </label>
          <button type="button" onClick={onClose} className="rounded p-1.5 hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="scrollbar-thin grid flex-1 grid-cols-3 gap-3 overflow-y-auto p-4 sm:grid-cols-4 md:grid-cols-6">
          {(rows || []).map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => onPick(m)}
              className="card-nq"
              title={m.original_name}
              data-testid="media-picker-item"
            >
              <SmartImage src={m.url} alt={m.alt} ratio="aspect-square" fit="contain" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
